import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { enqueueSnackbar } from "../../../components/GlobalSnackbar/snackbarStore";
import type { IncidentStatus } from "../../../types";
import { keys } from "./keys";

export type UpdateIncidentCommand = {
  id: string;
  subject_id: string;
  title: string;
  date: string;
  status: IncidentStatus;
  comment?: string | null;

  provider_name?: string | null;
  provider_id?: string | null;
  contact_name?: string | null;
};

async function updateIncident(command: UpdateIncidentCommand) {
  const { data, error } = await apiClient
    .from('incidents')
    .update({
      title: command.title,
      date: command.date,
      status: command.status,
      comment: command.comment ?? null,
      provider_name: command.provider_name ?? null,
      provider_id: command.provider_id ?? null,
      contact_name: command.contact_name ?? null,
    })
    .eq('id', command.id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  // Also update subject_actions if this incident originated from or is mirrored in subject_actions
  await apiClient
    .from('subject_actions')
    .update({
      title: command.title,
      date: command.date,
      comment: command.comment ?? null,
      provider_name: command.provider_name ?? null,
      provider_id: command.provider_id ?? null,
      contact_name: command.contact_name ?? null,
    })
    .eq('id', command.id);

  return data;
}

export function useUpdateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (command: UpdateIncidentCommand) => updateIncident(command),
    onSuccess: (_data, variables) => {
      enqueueSnackbar("Incident mis à jour avec succès.", "success");
      queryClient.invalidateQueries({ queryKey: keys.subTopic(variables.subject_id) });
      queryClient.invalidateQueries({ queryKey: keys.subTopicIncidents(variables.subject_id) });
      queryClient.invalidateQueries({ queryKey: [...keys.all, 'activityTimeline'] });
      queryClient.invalidateQueries({ queryKey: [...keys.all, 'entityDocuments'] });
    },
    onError: () => {
      enqueueSnackbar("Une erreur est survenue lors de la mise à jour de l'incident.", "error");
    },
  });
}
